import { Plus, Trash2 } from 'lucide-react';
import type { TrainingQuestionDef } from '@hcc/types';
import { Button } from '../../ui/Button';
import { SHARED_INPUT_CLASS } from './pageMeta';
import { QuestionCommonFields } from './QuestionCommonFields';
import { genId } from './moduleOps';

type MatchQuestion = Extract<TrainingQuestionDef, { type: 'matching' }>;

/**
 * Pairs editor for a matching question: each row is one correct
 * left -> right pairing; the player shuffles the right column.
 */
export function MatchQuestionEditor({
  question,
  onChange,
  graded = true,
}: {
  question: MatchQuestion;
  onChange: (next: MatchQuestion) => void;
  /** False hides the points box (an unmarked assessment). */
  graded?: boolean;
}) {
  function setPair(id: string, patch: { left?: string; right?: string }) {
    onChange({
      ...question,
      pairs: question.pairs.map((p) => (p.id === id ? { ...p, ...patch } : p)),
    });
  }

  return (
    <div className="space-y-3">
      <QuestionCommonFields
        prompt={question.prompt}
        points={question.points}
        onPrompt={(prompt) => onChange({ ...question, prompt })}
        onPoints={(points) => onChange({ ...question, points })}
        graded={graded}
      />
      <div className="space-y-2">
        <div className="flex gap-2 text-sm font-medium text-fg-muted">
          <span className="flex-1">Item</span>
          <span className="flex-1">Its correct match</span>
          <span className="w-8" />
        </div>
        {question.pairs.map((p) => (
          <div key={p.id} className="flex items-center gap-2">
            <input
              value={p.left}
              onChange={(e) => setPair(p.id, { left: e.target.value })}
              placeholder="e.g. Yellow triangle"
              className={`${SHARED_INPUT_CLASS} mt-0 flex-1`}
            />
            <input
              value={p.right}
              onChange={(e) => setPair(p.id, { right: e.target.value })}
              placeholder="e.g. Warning"
              className={`${SHARED_INPUT_CLASS} mt-0 flex-1`}
            />
            <button
              type="button"
              aria-label="Remove pair"
              disabled={question.pairs.length <= 2}
              onClick={() =>
                onChange({ ...question, pairs: question.pairs.filter((x) => x.id !== p.id) })
              }
              className="rounded-token-sm p-1.5 text-fg-muted hover:bg-surface-2 disabled:opacity-30"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        <Button
          variant="ghost"
          size="sm"
          iconLeft={<Plus className="h-4 w-4" />}
          onClick={() =>
            onChange({
              ...question,
              pairs: [...question.pairs, { id: genId('pair'), left: '', right: '' }],
            })
          }
        >
          Add pair
        </Button>
      </div>
    </div>
  );
}
